import { useState } from 'react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from '~/primitives/dropdown-menu';
import { cn } from '~/util';
import { useFieldContext } from '~/util/hooks/useFormContext';
import {
    FieldLabelWithError,
    FieldWithErrorProps,
} from './FieldLabelWithError';
import { useDisableFormContext } from '../DisableContext';
import { InteractableDataWrapper } from '~/partials/InteractableDataWrapper';

export function DropdownBooleanField({
    optionDisabled,
    optionEnabled,
    placeholder,
    classname,
    allowNull = false,
    dropdownOpen,
    setDropdownOpen,
    preventCloseAutoFocus = false,
    ...labelProps
}: {
    optionDisabled: string;
    optionEnabled: string;
    placeholder: string;
    classname?: string;
    allowNull?: boolean;
    dropdownOpen?: boolean;
    setDropdownOpen?: (open: boolean) => void;
    preventCloseAutoFocus?: boolean;
} & FieldWithErrorProps) {
    const field = useFieldContext<boolean | null>();
    const [internalDropdownOpen, setInternalDropdownOpen] = useState(false);

    const [usedDropdownOpen, setUsedDropdownOpen] =
        dropdownOpen !== undefined && setDropdownOpen !== undefined
            ? [dropdownOpen, setDropdownOpen]
            : [internalDropdownOpen, setInternalDropdownOpen];

    const disabled = useDisableFormContext();

    return (
        <FieldLabelWithError<boolean | null> {...labelProps}>
            <DropdownMenu
                open={usedDropdownOpen}
                onOpenChange={setUsedDropdownOpen}
            >
                <DropdownMenuTrigger disabled={disabled}>
                    <InteractableDataWrapper
                        id={field.name}
                        disabled={disabled}
                        open={usedDropdownOpen}
                        className={cn(
                            field.state.value != undefined
                                ? 'text-secondary-foreground'
                                : 'text-secondary-light',
                            classname,
                            disabled && 'opacity-50 cursor-not-allowed',
                        )}
                        required={labelProps.required}
                    >
                        {field.state.value == undefined
                            ? placeholder
                            : field.state.value
                              ? optionEnabled
                              : optionDisabled}
                    </InteractableDataWrapper>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="start"
                    onCloseAutoFocus={
                        preventCloseAutoFocus
                            ? (event) => event.preventDefault()
                            : undefined
                    }
                >
                    <DropdownMenuRadioGroup
                        value={
                            field.state.value == null ||
                            field.state.value == undefined
                                ? ''
                                : field.state.value
                                  ? optionEnabled
                                  : optionDisabled
                        }
                        onValueChange={(value) => {
                            field.handleChange(
                                value === optionEnabled
                                    ? true
                                    : value === optionDisabled
                                      ? false
                                      : null,
                            );
                            setUsedDropdownOpen(false);
                        }}
                    >
                        {allowNull && (
                            <DropdownMenuRadioItem
                                value=""
                                className="text-lg cursor-pointer"
                            >
                                {placeholder}
                            </DropdownMenuRadioItem>
                        )}
                        <DropdownMenuRadioItem
                            value={optionDisabled}
                            className="text-lg cursor-pointer"
                        >
                            {optionDisabled}
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem
                            value={optionEnabled}
                            className="text-lg cursor-pointer"
                        >
                            {optionEnabled}
                        </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                </DropdownMenuContent>
            </DropdownMenu>
        </FieldLabelWithError>
    );
}
